import { computed } from 'vue'
import pusherService from '@/services/PusherService'
import { useAuth } from '@/services/AuthService'
import { useChatStore } from '@/stores/chatsStore'
import { useRouter } from 'vue-router'

export function usePusher() {

    const { user, isAuthenticated } = useAuth()
    const router = useRouter()

    const isConnected = computed(() => pusherService.getConnectionStatus())
    const isSubscribed = computed(() => pusherService.getSubscriptionStatus())
    const currentChannel = computed(() => pusherService.getUserChannel()?.name)
    const conversationChannels = computed(() => Array.from(pusherService.getConversationChannels().values()).map(c => c.name))

    // Expected channels (show even if not subscribed)
    const chatStore = useChatStore()
    const expectedUserChannel = computed(() => user.value ? `user.${(user.value as any).id}` : '')
    const expectedConversationChannel = computed(() => chatStore.currentChat ? `conversation.${chatStore.currentChat.id}` : '')

    // Subscription flags
    const isUserChannelSubscribed = computed(() => !!pusherService.getUserChannel())
    const isConversationChannelSubscribed = computed(() => {
        return chatStore?.currentChat?.id
    })

    const subscribeToUserChannel = (userId: string | number) => {
        // Set up navigation callbacks before subscribing
        pusherService.setNavigationCallbacks({
            navigateToCheckinForm: (checkinId: number) => {
                router.push(`/checkin/${checkinId}`)
            },
            navigateToIntakeForm: (intakeId: number) => {
                router.push(`/intake/${intakeId}`)
            },
            navigateToCheckinsPage: () => {
                router.push('/checkins')
            },
            navigateToCoachNotes: () => {
                router.push('/account/settings?activeTab=profile&showNotes=true')
            },
            navigateToHabit: (habitId: string, date: string) => {
                router.push(`/habit/${habitId}?date=${date}`)
            }
        })

        return pusherService.subscribeToUserChannel(userId)
    }

    const unsubscribeFromUserChannel = () => {
        pusherService.unsubscribeFromUserChannel()
    }

    const disconnect = () => {
        pusherService.disconnect()
    }

    const subscribeToCurrentUser = () => {
        if (user.value && (user.value as any)?.id && isAuthenticated.value) {
            return subscribeToUserChannel((user.value as any).id)
        }
        return false
    }

    return {
        // State
        isConnected,
        isSubscribed,
        currentChannel,
        conversationChannels,
        expectedUserChannel,
        expectedConversationChannel,
        isUserChannelSubscribed,
        isConversationChannelSubscribed,

        // Methods
        subscribeToUserChannel,
        unsubscribeFromUserChannel,
        disconnect,
        subscribeToCurrentUser,

        // Direct access to service
        pusherService
    }
}
